import React, { useContext, useEffect, useState } from "react";

import CenterIcon from "../../components/ui/CenterIcon";

import profile from "../../assets/profile.svg";
import AuthContext from "../../context/AuthContext";
import FlexRowReverse from "../../components/ui/FlexRowReverse";
import { profileStatistics } from "../../services/defaultSettings";
import LoadingIcon from "../../components/ui/LoadingIcon";
import ProgressCircle from "../../components/ui/ProgressCircle";
import { printUnit } from "../../utils/ar";
import auth from "../../services/authServices";
import http from "../../services/httpServices";
import { useLanguage } from "../../context/LanguageContext";

const UserHome = () => {
    const { language } = useLanguage();
    const { user } = useContext(AuthContext);
    const [statistics, setStatistics] = useState({});
    const [isProfileStatisticsLoading, setIsProfileStatisticsLoading] = useState(true);

    const getUserStatistics = async () => {
        setIsProfileStatisticsLoading(true);
        try {
            const token = auth.getToken();
            const config = auth.getAuthConfig(token);
            const { data: result } = await http.get("/api/user/statistics", config);
            setStatistics(result);

            setIsProfileStatisticsLoading(false);
        } catch (error) {
            setIsProfileStatisticsLoading(false);
        }
    };

    useEffect(() => {
        getUserStatistics();
    }, []);
    const content = {
        profileTitle: {
            en: "Your Profile",
            ar: "ملفك الشخصي",
            de: "Ihr Profil",
        },
        statisticsTitle: {
            en: "Your Course Statistics",
            ar: "إحصائيات كورساتك",
            de: "Ihre Kursstatistiken",
        },
        platformStatsTitle: {
            en: "Your Platform Statistics",
            ar: "إحصائياتك على المنصة",
            de: "Ihre Plattformstatistiken",
        },
        watchedVideos: {
            en: "Videos Watched",
            ar: "عدد الفيديوهات التي شاهدتها",
            de: "Angesehene Videos",
        },
        completedExams: {
            en: "Completed Exams",
            ar: "عدد الاختبارات التي أنهيتها",
            de: "Abgeschlossene Prüfungen",
        },
        averageResults: {
            en: "Your Average Results",
            ar: "متوسط النتائج التي حصلت عليها",
            de: "Ihre Durchschnittsergebnisse",
        },
        totalDuration: {
            en: "Total Time Spent Watching Lectures",
            ar: "إجمالي مدة فتح المحاضرات",
            de: "Gesamtzeit für Vorlesungen",
        },
        totalExamAttempts: {
            en: "Total Exam Attempts",
            ar: "إجمالي عدد مرات فتح الاختبار",
            de: "Gesamtanzahl der Prüfungsversuche",
        },
        totalExamCompletions: {
            en: "Total Exams Completed",
            ar: "إجمالي عدد الاختبارات التي تم إنهاؤها",
            de: "Gesamtanzahl der abgeschlossenen Prüfungen",
        },
        loadingStats: {
            en: "Loading your statistics...",
            ar: "جاري تحميل الإحصائيات...",
            de: "Statistiken werden geladen...",
        },
    };
    return (
        <>
            <div
                className={`flex flex-col ${
                    language === "ar" ? "sm:flex-row" : "sm:flex-row-reverse"
                } sm:space-x-8`}
            >
                <div className="sm:basis-1/4 p-2">
                    <img src={profile} alt="profile" />
                </div>
                <div className="sm:basis-3/4">
                    <div
                        className={`text-2xl ${
                            language === "ar" ? "font-bold" : "font-mont font-medium"
                        }`}
                    >
                        {user.full_name}
                    </div>
                    <div className="h-1 w-full bg-outer-container rounded-full my-5 smooth"></div>
                    <div>
                        <FlexRowReverse>
                            <CenterIcon
                                icon={"ant-design:phone-twotone"}
                                className="text-cyan-500"
                            />
                            <span className="flex-center-both">
                                {(user.phone + "").padStart(11, "0")}
                            </span>
                        </FlexRowReverse>
                        <FlexRowReverse>
                            <CenterIcon
                                icon={"ic:sharp-alternate-email"}
                                className="text-yellow-400"
                            />
                            <span className="flex-center-both">{user.email}</span>
                        </FlexRowReverse>
                    </div>
                </div>
            </div>

            {profileStatistics ? (
                !isProfileStatisticsLoading ? (
                    <div className="pt-10 flex-center-both flex-col space-y-8">
                        <div>
                            <div className="max-w-lg w-full mx-auto h-1 bg-cyan-500 bg-opacity-30 smooth mb-5 rounded-md"></div>
                            <div className="w-full flex-center-both space-x-2 space-x-reverse pb-10">
                                <CenterIcon
                                    icon={"arcticons:questionnaire-star"}
                                    className="font-big text-cyan-500"
                                />
                                <div
                                    className={`font-h1 ${
                                        language === "ar" ? "font-bold" : "font-mont font-medium"
                                    }`}
                                >
                                    {content.statisticsTitle[language]}
                                </div>
                                <CenterIcon
                                    icon={"arcticons:questionnaire-star"}
                                    className="font-big text-cyan-500"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
                                <ProgressCircle
                                    color="rose"
                                    unit="فيديو"
                                    title={content.watchedVideos[language]}
                                    value={statistics.viewed_videos_count}
                                    max={statistics.total_videos_count}
                                    textsIndex={3}
                                />
                                <ProgressCircle
                                    color="cyan"
                                    unit="امتحان"
                                    title={content.completedExams[language]}
                                    value={statistics.finished_exams_count}
                                    max={statistics.total_exams_count}
                                    textsIndex={2}
                                />
                                <ProgressCircle
                                    color="purple"
                                    unit={false}
                                    title={content.averageResults[language]}
                                    value={statistics.total_results}
                                    max={statistics.total_full_result}
                                    textsIndex={1}
                                />
                            </div>
                        </div>

                        <div className="w-full flex-center-both space-x-2 space-x-reverse pb-10">
                            <CenterIcon
                                icon={"arcticons:questionnaire-star"}
                                className="font-big text-cyan-500"
                            />
                            <div
                                className={`font-h1 ${
                                    language === "ar" ? "font-bold" : "font-mont font-medium"
                                }`}
                            >
                                {content.platformStatsTitle[language]}
                            </div>
                            <CenterIcon
                                icon={"arcticons:questionnaire-star"}
                                className="font-big text-cyan-500"
                            />
                        </div>

                        <div className="flex-center-both flex-col w-full">
                            <InfoRow
                                color="rose"
                                unit="دقيقة"
                                title={content.totalDuration[language]}
                                value={Math.ceil(statistics.total_video_open_duration / 60)}
                            />
                            <InfoRow
                                color="cyan"
                                unit="مرة"
                                title={content.totalExamAttempts[language]}
                                value={statistics.total_exam_results_count}
                            />
                            <InfoRow
                                color="purple"
                                unit="مرة"
                                title={content.totalExamCompletions[language]}
                                value={statistics.finished_exam_results_count}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="p-10 rounded-md bg-outer-container border flex-center-both clr-text-primary">
                        <LoadingIcon />
                        <span>{content.loadingStats[language]}</span>
                    </div>
                )
            ) : null}
        </>
    );
};

export default UserHome;

const InfoRow = ({
    title = "اجمالي عدد مران",
    value = 0,
    unit = "ساعة",
    color = "blue",
    isDouble = false,
    className,
}) => {
    className += ` bg-blue-500`;
    if (color === "rose") {
        className += ` bg-rose-500`;
    } else if (color === "emerald") {
        className += ` bg-emerald-500`;
    } else if (color === "purple") {
        className += ` bg-purple-500`;
    } else if (color === "teal") {
        className += ` bg-teal-500`;
    } else if (color === "yellow") {
        className += ` bg-yellow-500`;
    } else if (color === "cyan") {
        className += ` bg-cyan-500`;
    } else if (color === "slate") {
        className += ` bg-slate-500`;
    }
    return (
        <div className="flex-center-both flex-col w-full">
            <div className="flex space-x-2 space-x-reverse items-center justify-between max-w-xl w-full">
                <div>{title}</div>
                <div className={`rounded-full p-1 ${className}`}>
                    <span className="bg-inner-container clr-text-primary smooth rounded-full py-1 px-2 font-w-bold flex text-center">
                        {printUnit(value, unit)}
                    </span>
                </div>
            </div>
            {isDouble ? (
                <>
                    <div className="flex-center-both px-2 w-full pt-5">
                        <div className="max-w-md w-full bg-outer-container smooth h-1 rounded-full"></div>
                    </div>
                    <div className="flex-center-both px-2 pb-5 pt-2 w-full">
                        <div className="max-w-md w-full bg-outer-container smooth h-1 rounded-full"></div>
                    </div>
                </>
            ) : (
                <div className="flex-center-both px-2 py-5 w-full">
                    <div className="max-w-md w-full bg-outer-container smooth h-1 rounded-full"></div>
                </div>
            )}
        </div>
    );
};
